/**
 * Phase 290.c — Weekly Goal Capture Page
 * =======================================
 * Public page (no login) reached from the Monday digest CTA at
 *   /goals/:token
 *
 * Flow:
 *   1. On mount → call GET /api/weekly-goals/verify?token=... to confirm
 *      the token is valid + fetch display context (owner_name, academy_name,
 *      existing_goal if already submitted this week).
 *   2. Owner types a single goal (max 280 chars).
 *   3. Submit → POST /api/weekly-goals/submit { token, goal_text }
 *   4. Show success state + a soft note that Claude will reference this
 *      next Monday.
 *
 * Design tokens: slate-900 text, emerald primary, amber goal accent —
 * matches the digest email exactly so the page feels like a continuation.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Star, Sparkles, CheckCircle2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOAL_MAX_LEN = 280;

export default function WeeklyGoalCapture() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading | form | done | error
  const [ctx, setCtx] = useState(null);
  const [goal, setGoal] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const verify = useCallback(async () => {
    setPhase("loading");
    try {
      const res = await axios.get(`${API}/weekly-goals/verify`, {
        params: { token },
      });
      setCtx(res.data);
      setGoal(res.data.existing_goal || "");
      setPhase("form");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      setErrorMsg(
        detail === "invalid_or_expired_token"
          ? "This link has expired or is invalid. Open this week's digest email to get a fresh one."
          : "We couldn't load your goal page. Try again from the digest email."
      );
      setPhase("error");
    }
  }, [token]);

  useEffect(() => {
    if (token) verify();
  }, [token, verify]);

  const handleSubmit = async () => {
    const trimmed = (goal || "").trim();
    if (trimmed.length < 4) {
      toast.error("Type a goal first (at least 4 characters)");
      return;
    }
    if (trimmed.length > GOAL_MAX_LEN) {
      toast.error(`Keep your goal under ${GOAL_MAX_LEN} characters`);
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/weekly-goals/submit`, {
        token,
        goal_text: trimmed,
      });
      setPhase("done");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "invalid_or_expired_token") {
        setErrorMsg(
          "This link expired while you were typing. Open this week's digest email to get a fresh link."
        );
        setPhase("error");
      } else {
        toast.error("Failed to save — try again in a moment");
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────
  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center px-4">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading your goal page…</span>
        </div>
      </div>
    );
  }

  // ── Error / expired ───────────────────────────────────────────────────
  if (phase === "error") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-amber-200 p-8 shadow-sm">
          <h1 className="text-xl font-bold text-slate-900 mb-2">
            Link unavailable
          </h1>
          <p className="text-sm text-slate-600 leading-relaxed">{errorMsg}</p>
          <a
            href="https://out-play.in"
            className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-sm mt-6 hover:underline"
            data-testid="goal-error-home-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Out-Play
          </a>
        </div>
      </div>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────
  if (phase === "done") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center px-4">
        <div
          className="max-w-md w-full bg-white rounded-2xl border border-emerald-200 p-8 shadow-sm text-center"
          data-testid="goal-success-card"
        >
          <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <CheckCircle2 className="h-7 w-7 text-emerald-700" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Goal locked in 🎯
          </h1>
          <p className="text-sm text-slate-600 leading-relaxed mb-5">
            Your AI COO will reference this exact goal in next Monday's digest
            and call out how the week's numbers measured up.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
            <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-1">
              ⭐ This week's goal
            </p>
            <p className="text-sm text-slate-900 leading-snug">"{goal.trim()}"</p>
          </div>
          <a
            href="https://out-play.in/dashboard"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-700 to-teal-600 text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:from-emerald-800 hover:to-teal-700 transition-colors"
            data-testid="goal-success-dashboard-link"
          >
            Open Dashboard →
          </a>
        </div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────
  const charsLeft = GOAL_MAX_LEN - (goal || "").length;
  const isUpdate = !!(ctx && ctx.existing_goal);
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 px-4 py-10">
      <div
        className="max-w-xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
        data-testid="goal-capture-card"
      >
        {/* Hero */}
        <div className="bg-gradient-to-br from-slate-900 to-emerald-800 px-7 py-7 text-white">
          <div className="flex items-center gap-2 mb-3 text-emerald-200">
            <Star className="h-4 w-4 fill-amber-300 text-amber-300" />
            <p className="text-[11px] uppercase tracking-[0.15em] font-bold">
              Out-Play · Weekly Goal
            </p>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight">
            Hey {ctx?.owner_name || "there"} 👋
          </h1>
          <p className="text-emerald-100 text-sm mt-2 leading-relaxed">
            What's <strong className="text-white">one thing</strong>{" "}
            {ctx?.academy_name || "your academy"} should achieve this week?
          </p>
          <p className="text-emerald-200 text-[11px] mt-1.5 font-mono">
            Week {ctx?.week_iso}
          </p>
        </div>

        {/* Body */}
        <div className="px-7 py-7">
          <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-2">
            My #1 goal for this week
          </label>
          <Textarea
            value={goal}
            onChange={(e) => setGoal(e.target.value.slice(0, GOAL_MAX_LEN))}
            placeholder="e.g. Collect ₹40,000 in pending fees by Friday and run 3 high-attendance sessions"
            rows={4}
            className="text-sm leading-relaxed border-slate-300 focus-visible:ring-emerald-500"
            data-testid="goal-textarea"
            maxLength={GOAL_MAX_LEN}
          />
          <div className="flex items-center justify-between mt-2 mb-5">
            <p className="text-[11px] text-slate-500">
              One line. Specific. Measurable.
            </p>
            <p
              className={`text-[11px] font-mono ${
                charsLeft < 30 ? "text-amber-600" : "text-slate-400"
              }`}
              data-testid="goal-chars-left"
            >
              {charsLeft} left
            </p>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={saving || (goal || "").trim().length < 4}
            className="w-full bg-gradient-to-r from-emerald-700 to-teal-600 text-white font-semibold hover:from-emerald-800 hover:to-teal-700"
            data-testid="goal-submit-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving…
              </>
            ) : isUpdate ? (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Update my goal
              </>
            ) : (
              <>
                <Star className="h-4 w-4 mr-2 fill-amber-300 text-amber-300" />
                Lock in my goal
              </>
            )}
          </Button>

          <p className="text-[11px] text-slate-500 leading-relaxed mt-5 text-center">
            Your AI COO will reference this in next Monday's digest and tell
            you exactly how the numbers measured up. No spam, no extra emails.
          </p>
        </div>
      </div>
    </div>
  );
}
